import { FormEvent, useState } from "react";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { useChangePassword } from "../../utils/hooks/usePasswordChange";
import { useSignin } from "../../utils/hooks/useSignIn";
import { useErrorToast, useSuccessToast } from "../../utils/showToast";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import {
  AUTH_PASSWORD_TOO_LONG_TEXT,
  AUTH_PASSWORD_TOO_SHORT_TEXT,
  CONFIRM_TEXT,
} from "../../utils/constants/i18n-strings";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Collapsible,
  CollapsibleContent,
  Heading,
  IconButton,
  Input,
  Label,
  VStack,
} from "@wootingkb/component-library";
import { useBoolean } from "../../utils/hooks/useBoolean";
import {
  MAX_PASSWORD_LENGTH,
  MIN_PASSWORD_LENGTH,
} from "../../utils/constants/auth";

const PASSWORD_CHANGED_TOAST = msg({
  id: "User_password_changed_toast",
  message: "Password changed.",
});

const TOGGLE_PASSWORD_ARIA_LABEL = msg({
  id: "User_toggle_change_password_aria_label",
  message: "Toggle change password dropdown",
});

export default function ChangePassword() {
  const showErrorToast = useErrorToast();
  const showSuccessToast = useSuccessToast();
  const [curPass, setCurPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confPass, setConfPass] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [flag, setFlag] = useBoolean();
  const { signIn } = useSignin(false);
  const { changePw } = useChangePassword(false);
  const { _: i18n } = useLingui();

  function resetForm() {
    setCurPass("");
    setNewPass("");
    setConfPass("");
  }

  async function changePassword(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (newPass.length < MIN_PASSWORD_LENGTH) {
      showErrorToast(i18n(AUTH_PASSWORD_TOO_SHORT_TEXT));
      return;
    }
    if (newPass.length > MAX_PASSWORD_LENGTH) {
      showErrorToast(i18n(AUTH_PASSWORD_TOO_LONG_TEXT));
      return;
    }

    setIsLoading(true);
    changePw(curPass, newPass, confPass)
      .then((email) => {
        signIn(email, newPass).then(() =>
          showSuccessToast(i18n(PASSWORD_CHANGED_TOAST))
        );
      })
      .catch((e) => {
        showErrorToast(e);
      })
      .finally(() => {
        resetForm();
        setIsLoading(false);
      });
  }

  return (
    <Card>
      <CardHeader className="flex-row justify-between">
        <Heading as="h3" size="h4" weight="semibold">
          <Trans id="User_change_password_title">Change Password</Trans>
        </Heading>
        <IconButton
          variant="muted"
          aria-label={i18n(TOGGLE_PASSWORD_ARIA_LABEL)}
          onClick={setFlag.toggle}
        >
          {flag ? <BsChevronUp /> : <BsChevronDown />}
        </IconButton>
      </CardHeader>
      <CardContent>
        <Collapsible
          open={flag}
          onOpenChange={(open) => (open ? setFlag.on() : setFlag.off())}
        >
          <CollapsibleContent>
            <form
              onSubmit={changePassword}
              className="flex flex-col gap-4 px-1"
            >
              <VStack className="gap-1">
                <Label htmlFor="current-password" weight="semibold" required>
                  <Trans id="User_current_password_label">
                    Current Password
                  </Trans>
                </Label>
                <Input
                  type="password"
                  name="current-password"
                  id="current-password"
                  required
                  onChange={(e) => setCurPass(e.target.value)}
                  value={curPass}
                  autoComplete="current-password"
                />
              </VStack>
              <VStack className="gap-1">
                <Label htmlFor="new-password" weight="semibold" required>
                  <Trans id="User_new_password_label">New Password</Trans>
                </Label>
                <Input
                  type="password"
                  name="new-password"
                  id="new-password"
                  required
                  onChange={(e) => setNewPass(e.target.value)}
                  value={newPass}
                  autoComplete="new-password"
                />
              </VStack>
              <VStack className="gap-1">
                <Label htmlFor="confirm-password" weight="semibold" required>
                  <Trans id="User_confirm_password_label">
                    Confirm Password
                  </Trans>
                </Label>
                <Input
                  type="password"
                  name="confirm-password"
                  id="confirm-password"
                  required
                  onChange={(e) => setConfPass(e.target.value)}
                  value={confPass}
                  autoComplete="new-password"
                />
              </VStack>
              <Button
                type="submit"
                variant="secondary"
                size="sm"
                isLoading={isLoading}
                className="self-end"
              >
                {i18n(CONFIRM_TEXT)}
              </Button>
            </form>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}
